import { useEffect, useState } from 'react';
import { deleteClassroom, renameClassroom, setClassroomLanguages, setClassroomMeeting, setOceanFree } from '../../lib/api';
import { DAYS, cleanMeetUrl, providerOf } from '../../lib/meeting';
import LanguagePicker from './LanguagePicker';
import type { Classroom } from '../../lib/types';
import { useClassroom } from '../../state/Classroom';

/** Rename or delete the open class. Deleting asks for the class name, typed. */
export default function ClassSettings({ c }: { c: Classroom }) {
  const { refresh, classrooms, setActiveId, students, assignments } = useClassroom();
  const [name, setName] = useState(c.name);
  const [confirm, setConfirm] = useState('');
  const [language, setLanguage] = useState(c.language);
  const [nativeLanguage, setNativeLanguage] = useState(c.native_language);
  const langChanged = language !== c.language || nativeLanguage !== c.native_language;

  const saveLanguages = async () => {
    if (language === nativeLanguage) { setMsg({ ok: false, text: 'Pick two different languages.' }); return; }
    setBusy(true); setMsg(null);
    try {
      await setClassroomLanguages(c.id, language, nativeLanguage);
      await refresh();
      setMsg({ ok: true, text: 'Languages saved.' });
    } catch (err) {
      setMsg({ ok: false, text: err instanceof Error ? err.message : String(err) });
    } finally { setBusy(false); }
  };
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const rename = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || name.trim() === c.name) return;
    setBusy(true); setMsg(null);
    try {
      await renameClassroom(c.id, name.trim());
      await refresh();
      setMsg({ ok: true, text: 'Renamed.' });
    } catch (err) {
      setMsg({ ok: false, text: err instanceof Error ? err.message : String(err) });
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (confirm.trim() !== c.name) return;
    setBusy(true); setMsg(null);
    try {
      await deleteClassroom(c.id);
      const next = classrooms.find(x => x.id !== c.id);
      if (next) setActiveId(next.id);
      await refresh();
    } catch (err) {
      setMsg({ ok: false, text: err instanceof Error ? err.message : String(err) });
      setBusy(false);
    }
  };

  return (
    <div className="panel class-settings">
      <form onSubmit={rename} className="field">
        <label htmlFor="rename">Class name</label>
        <div className="row" style={{ flexWrap: 'nowrap' }}>
          <input id="rename" maxLength={80} value={name} onChange={e => setName(e.target.value)} />
          <button disabled={busy || !name.trim() || name.trim() === c.name}>Rename</button>
        </div>
      </form>

      <div>
        <LanguagePicker language={language} nativeLanguage={nativeLanguage}
          onChange={(l, n) => { setLanguage(l); setNativeLanguage(n); }} />
        {langChanged && (
          <div className="row">
            <button className="primary" disabled={busy} onClick={saveLanguages}>Save languages</button>
            <span className="small muted">Existing videos keep the subtitles they already have.</span>
          </div>
        )}
      </div>

      <MeetingSettings c={c} />

      <OceanAccess c={c} />

      <div className="danger-zone">
        <strong>Delete this class</strong>
        <p className="small muted">
          Deletes the class, its {assignments.length} assignment{assignments.length === 1 ? '' : 's'} and all
          progress on them. The {students.length} student account{students.length === 1 ? '' : 's'} in it
          are not deleted. They just leave this class. This can't be undone.
        </p>
        <div className="field">
          <label htmlFor="confirm-delete">Type <strong>{c.name}</strong> to confirm</label>
          <input id="confirm-delete" value={confirm} onChange={e => setConfirm(e.target.value)} autoComplete="off" />
        </div>
        <button className="danger solid" disabled={busy || confirm.trim() !== c.name} onClick={remove}>
          {busy ? 'Deleting…' : 'Delete class'}
        </button>
      </div>

      {msg && <div className={`notice small ${msg.ok ? 'good' : 'bad'}`} style={{ marginTop: 12 }}>{msg.text}</div>}
    </div>
  );
}

/** Whether students must do homework before the class ocean opens each day. */
function OceanAccess({ c }: { c: Classroom }) {
  const { refresh } = useClassroom();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const free = !!c.ocean_free;
  const pick = async (v: boolean) => {
    if (v === free) return;
    setBusy(true); setErr(null);
    try { await setOceanFree(c.id, v); await refresh(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="field" id="ocean-access">
      <label>🌊 The class ocean</label>
      <div className="tf-pick">
        <button type="button" className={!free ? 'on' : ''} aria-pressed={!free} disabled={busy} onClick={() => void pick(false)}>After homework</button>
        <button type="button" className={free ? 'on' : ''} aria-pressed={free} disabled={busy} onClick={() => void pick(true)}>Open for free</button>
      </div>
      <span className="small muted">
        {free
          ? 'Students can go into the ocean any time.'
          : 'Each day, a student finishes at least one assignment (a lesson, video or story that counts) before the ocean opens for them.'}
      </span>
      {err && <span className="small" style={{ color: 'var(--bad)' }}>{err}</span>}
    </div>
  );
}

/** The class's live meeting link (Google Meet, Zoom…) and, optionally, its weekly time. */
function MeetingSettings({ c }: { c: Classroom }) {
  const { refresh } = useClassroom();
  const [url, setUrl] = useState(c.meet_url ?? '');
  const [day, setDay] = useState<string>(c.meet_day == null ? '' : String(c.meet_day));
  const [time, setTime] = useState(c.meet_time ?? '');
  const [minutes, setMinutes] = useState(c.meet_minutes ?? 60);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // Arriving from the dashboard's "Add your meeting link": bring it into view.
  useEffect(() => {
    if (window.location.hash !== '#meeting') return;
    const el = document.getElementById('meeting');
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    (document.getElementById('meet-url') as HTMLInputElement | null)?.focus({ preventScroll: true });
  }, []);

  const cleaned = cleanMeetUrl(url);
  const changed = (url.trim() ? cleaned : null) !== (c.meet_url ?? null) || day !== (c.meet_day == null ? '' : String(c.meet_day))
    || time !== (c.meet_time ?? '') || minutes !== (c.meet_minutes ?? 60);
  const tz = c.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const save = async (clear = false) => {
    if (!clear && url.trim() && !cleaned) { setMsg({ ok: false, text: 'That link doesn’t look right. Paste the whole https:// link from Google Meet or Zoom.' }); return; }
    if (!clear && (day === '') !== (time === '')) { setMsg({ ok: false, text: 'Pick both a day and a time, or neither.' }); return; }
    setBusy(true); setMsg(null);
    try {
      const m = clear
        ? { meet_url: null, meet_day: null, meet_time: null, meet_minutes: null }
        : { meet_url: cleaned, meet_day: day === '' ? null : Number(day), meet_time: time || null, meet_minutes: day === '' ? null : minutes };
      await setClassroomMeeting(c.id, m);
      await refresh();
      if (clear) { setUrl(''); setDay(''); setTime(''); }
      setMsg({ ok: true, text: clear ? 'Meeting removed.' : 'Saved. Students see it at the top of their home page.' });
    } catch (err) {
      setMsg({ ok: false, text: err instanceof Error ? err.message : String(err) });
    } finally { setBusy(false); }
  };

  return (
    <div className="meeting-settings" id="meeting">
      <strong>Class meeting</strong>
      <p className="small muted">Your Google Meet or Zoom link. Students get a Join button on their home page, and a “Live now” alert when class starts.</p>
      <div className="field">
        <label htmlFor="meet-url">Meeting link</label>
        <input id="meet-url" value={url} onChange={e => setUrl(e.target.value)} spellCheck={false} inputMode="url"
          placeholder="https://meet.google.com/abc-defg-hij" />
        {url.trim() && (cleaned
          ? <span className="hint ok">✓ {providerOf(cleaned).name}</span>
          : <span className="hint warn">Paste the whole link, starting with https://</span>)}
      </div>
      <div className="row meeting-when-fields">
        <div className="field">
          <label htmlFor="meet-day">Every week on</label>
          <select id="meet-day" value={day} onChange={e => setDay(e.target.value)}>
            <option value="">No set day</option>
            {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
          </select>
        </div>
        <div className="field">
          <label htmlFor="meet-time">At</label>
          <input id="meet-time" type="time" value={time} onChange={e => setTime(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="meet-length">For</label>
          <select id="meet-length" value={minutes} onChange={e => setMinutes(Number(e.target.value))}>
            {[30, 45, 60, 75, 90, 120].map(n => <option key={n} value={n}>{n} minutes</option>)}
          </select>
        </div>
      </div>
      <span className="hint">Times are in {tz.replace(/_/g, ' ')}. Students in other time zones see it in their own time.</span>
      <div className="row" style={{ marginTop: 8 }}>
        <button className="primary" disabled={busy || !changed} onClick={() => void save()}>Save meeting</button>
        {c.meet_url && <button disabled={busy} onClick={() => void save(true)}>Remove</button>}
      </div>
      {msg && <div className={`notice small ${msg.ok ? 'good' : 'bad'}`}>{msg.text}</div>}
    </div>
  );
}
